import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, Alert, Chip,
  Select, MenuItem, Tooltip,
} from '@mui/material';
import {
  StyledTableContainer, TableSkeleton, FilterBar, FilterSelect, EmptyState,
} from '../components/TableShared';
import api from '../config/api';

const ff = "'Poppins', sans-serif";

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'REVIEWED', label: 'Reviewed' },
  { value: 'ACTIONED', label: 'Actioned' },
  { value: 'DISMISSED', label: 'Dismissed' },
];

const STATUS_COLORS = {
  PENDING: { bg: '#FFF3D0', fg: '#B7791F' },
  REVIEWED: { bg: '#E3F0FB', fg: '#2B6CB0' },
  ACTIONED: { bg: '#FED7D7', fg: '#C53030' },
  DISMISSED: { bg: '#EDF2F7', fg: '#718096' },
};

export default function UserReports() {
  const [reports, setReports] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('PENDING');

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (statusFilter !== 'ALL') params.status = statusFilter;
      const res = await api.get('/admin/user-reports', { params });
      const data = res.data.data || res.data;
      setReports(data.reports || []);
      setCounts(data.counts || {});
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to load reports. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/user-reports/${id}`, { status });
      fetchReports();
    } catch (err) {
      console.error('Error updating report:', err);
      setError('Failed to update report status.');
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : 'N/A');

  const nameOf = (u) => (u ? (u.fullName || u.email || u.id) : 'Unknown');

  return (
    <Box sx={{ fontFamily: ff }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      <FilterBar>
        <FilterSelect
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={STATUS_OPTIONS}
          minWidth={160}
        />
        <Box sx={{ fontFamily: ff, fontSize: 12, color: '#718096' }}>
          {counts.PENDING ? `${counts.PENDING} pending` : 'No pending reports'}
        </Box>
      </FilterBar>

      {loading ? (
        <TableSkeleton columns={6} rows={6} />
      ) : (
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Reported user</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>Reported by</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => {
                const sc = STATUS_COLORS[r.status] || STATUS_COLORS.DISMISSED;
                return (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Box sx={{ fontWeight: 600 }}>{nameOf(r.reported)}</Box>
                      <Box sx={{ fontSize: 11, color: '#718096' }}>{r.reported?.email}</Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={r.reason} size="small" sx={{ fontFamily: ff, bgcolor: '#F0F9FF', color: '#2B6CB0' }} />
                      {r.context && (
                        <Box sx={{ fontSize: 10, color: '#A0AEC0', mt: 0.5 }}>from {r.context}</Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      {r.details ? (
                        <Tooltip title={r.details} placement="top-start" arrow>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                            {r.details}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box sx={{ color: '#A0AEC0', fontStyle: 'italic' }}>—</Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>{nameOf(r.reporter)}</Box>
                      <Box sx={{ fontSize: 11, color: '#718096' }}>{r.reporter?.email}</Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{fmt(r.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Select
                        size="small"
                        value={r.status}
                        onChange={(e) => updateStatus(r.id, e.target.value)}
                        sx={{
                          fontFamily: ff, fontSize: 12, minWidth: 130,
                          bgcolor: sc.bg, color: sc.fg, fontWeight: 600,
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }}
                      >
                        {['PENDING', 'REVIEWED', 'ACTIONED', 'DISMISSED'].map((s) => (
                          <MenuItem key={s} value={s} sx={{ fontFamily: ff, fontSize: 13 }}>{s}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
              {reports.length === 0 && (
                <EmptyState colSpan={6} message="No reports in this view." />
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      )}
    </Box>
  );
}
